/**
 * React hook for extracting pain data from transcripts using AI
 *
 * Wraps the API client's extractFromTranscript method with TanStack Query mutation
 * for automatic state management and error handling.
 */

import { useMutation } from "@tanstack/react-query";
import { SpanStatusCode } from "@opentelemetry/api";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type {
  ExtractionResult,
  ExtractRequest,
} from "@floline/api-client/types";
import { API_URL } from "@/env";
import { getTracer } from "@/telemetry";
import { i18n } from "@/i18n";
import { mapConsentRequiredError } from "@/lib/consent-errors";

// ============================================================================
// API Client
// ============================================================================

/** API client configured for extraction requests */
const apiClient = createApiClient(
  API_URL
);

// ============================================================================
// Types
// ============================================================================

export interface ExtractionInput {
  /** Transcript text to extract from */
  transcript: string;
  /** Reference date for relative dates (YYYY-MM-DD), defaults to today */
  referenceDate?: string;
  /** User's timezone */
  timezone?: string;
  /** User's existing medication list for better normalization */
  knownMedications?: string[];
  /** Context about current update set for multi-turn conversation */
  context?: string;
}

export interface UseExtractionOptions {
  /** Callback when extraction succeeds */
  onSuccess?: (result: ExtractionResult) => void;
  /** Callback when extraction fails */
  onError?: (error: Error) => void;
}

export type ExtractionState = "idle" | "extracting" | "success" | "error";

export interface UseExtractionReturn {
  /** Extract pain data from a transcript */
  extract: (input: ExtractionInput) => Promise<ExtractionResult>;
  /** Whether extraction is in progress */
  isExtracting: boolean;
  /** Extraction state */
  extractionState: ExtractionState;
  /** The last successful extraction result */
  result: ExtractionResult | undefined;
  /** Extraction error if any */
  error: Error | null;
  /** Reset the extraction state */
  reset: () => void;
}

// ============================================================================
// Extraction Function
// ============================================================================

/**
 * Extract pain data from transcript using the API
 *
 * Wrapped in a journey span so the fetch auto-instrumentation (E2)
 * attaches as a child span, giving end-to-end visibility.
 *
 * @param input - Transcript and optional context hints
 * @returns Extraction result with pain locations, medications, and events
 */
async function extractFromTranscript(
  input: ExtractionInput
): Promise<ExtractionResult> {
  const tracer = getTracer("floline-web.journeys");
  return tracer.startActiveSpan("journey.extraction", async (span) => {
    // PII-safe attributes — no raw transcript text
    span.setAttributes({
      "journey.name": "extraction",
      "extraction.transcript_length": input.transcript.length,
      "extraction.has_context": !!input.context,
      "extraction.known_medications_count": input.knownMedications?.length ?? 0,
    });

    try {
      const request: ExtractRequest = {
        transcript: input.transcript,
        referenceDate: input.referenceDate,
        timezone: input.timezone,
        knownMedications: input.knownMedications,
        context: input.context,
      };

      const result = await apiClient.extractFromTranscript(request);

      // Record result size (PII-safe counts only)
      span.setAttributes({
        "extraction.pain_location_count": result.painLocations.reduce(
          (sum, p) => sum + p.locations.length,
          0
        ),
        "extraction.medication_count": result.medications.reduce(
          (sum, m) => sum + m.medications.length,
          0
        ),
        "extraction.event_count": result.events.length,
        "extraction.needs_clarification": result.needsClarification,
      });

      return result;
    } catch (error: unknown) {
      const consentError = mapConsentRequiredError(error, i18n.t.bind(i18n));
      if (consentError) {
        throw consentError;
      }

      // Record the error on the span (sanitized — no PII)
      const errName =
        error instanceof Error ? error.constructor.name : "UnknownError";
      const errMessage =
        error instanceof Error ? error.message : String(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: errMessage });
      span.recordException({ name: errName, message: errMessage });

      // Rethrow with more descriptive message for common errors
      if (error instanceof ApiClientError) {
        if (error.status === 401) {
          throw new Error(i18n.t("errors.signInToExtract"));
        }
        if (error.status === 503 || error.code === "SERVICE_UNAVAILABLE") {
          throw new Error(i18n.t("errors.extractionUnavailable"));
        }
        if (error.code === "MISSING_TRANSCRIPT") {
          throw new Error(i18n.t("errors.missingTranscript"));
        }
        if (error.code === "TRANSCRIPT_TOO_LONG") {
          throw new Error(
            i18n.t("errors.transcriptTooLong")
          );
        }
        if (error.code === "EXTRACTION_FAILED") {
          throw new Error(
            error.message || i18n.t("errors.extractionFailed")
          );
        }
        if (error.code === "INVALID_JSON_RESPONSE") {
          throw new Error(i18n.t("errors.invalidAiResponse"));
        }
      }
      throw error;
    } finally {
      span.end();
    }
  });
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for extracting pain data from transcripts using AI
 *
 * Uses Azure OpenAI to extract structured pain data from free-form text.
 * Extracts pain locations, medications, and significant events.
 *
 * @example
 * ```tsx
 * const { extract, isExtracting, result, error } = useExtraction({
 *   onSuccess: (result) => {
 *     console.log('Extracted:', result.painLocations, result.medications);
 *   },
 *   onError: (error) => {
 *     console.error('Extraction failed:', error);
 *   },
 * });
 *
 * // Extract from transcript
 * const result = await extract({
 *   transcript: "My back is killing me today, about 7 out of 10. I took ibuprofen this morning.",
 *   referenceDate: "2026-01-11",
 * });
 * ```
 */
export function useExtraction(
  options: UseExtractionOptions = {}
): UseExtractionReturn {
  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: extractFromTranscript,
    onSuccess,
    onError,
  });

  // Derive extraction state from mutation status
  const extractionState: ExtractionState = mutation.isPending
    ? "extracting"
    : mutation.isSuccess
      ? "success"
      : mutation.isError
        ? "error"
        : "idle";

  return {
    extract: mutation.mutateAsync,
    isExtracting: mutation.isPending,
    extractionState,
    result: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ============================================================================
// Query Keys (for potential future use with caching)
// ============================================================================

/** Query key factory for extractions */
export const extractionKeys = {
  all: ["extractions"] as const,
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if an extraction result has any extracted data
 */
export function hasExtractionData(result: ExtractionResult): boolean {
  return (
    result.painLocations.some((p) => p.locations.length > 0) ||
    result.medications.some((m) => m.medications.length > 0) ||
    result.events.length > 0
  );
}

/**
 * Get the total count of extracted items
 */
export function getExtractionItemCount(result: ExtractionResult): number {
  const painCount = result.painLocations.reduce(
    (sum, p) => sum + p.locations.length,
    0
  );
  const medCount = result.medications.reduce(
    (sum, m) => sum + m.medications.length,
    0
  );
  const eventCount = result.events.length;

  return painCount + medCount + eventCount;
}

/**
 * Check if any items need user review (low confidence)
 */
export function needsUserReview(result: ExtractionResult): boolean {
  return result.lowConfidenceItems.length > 0 || result.needsClarification;
}

/**
 * Get confidence level label from confidence score
 */
export function getConfidenceLevelLabel(confidence: number): string {
  if (confidence >= 0.9) return "High confidence";
  if (confidence >= 0.7) return "Medium confidence";
  return "Low confidence";
}

/**
 * Get CSS class for confidence level styling
 */
export function getConfidenceColorClass(confidence: number): string {
  if (confidence >= 0.9) return "text-green-600 dark:text-green-400";
  if (confidence >= 0.7) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

/**
 * Get background CSS class for confidence level
 */
export function getConfidenceBackgroundClass(confidence: number): string {
  if (confidence >= 0.9) return "bg-green-50 dark:bg-green-900/20";
  if (confidence >= 0.7) return "bg-yellow-50 dark:bg-yellow-900/20";
  return "bg-red-50 dark:bg-red-900/20";
}
